import React, { useState } from 'react';
import PropTypes from 'prop-types';
import i18n from 'micro-i18n';
import { connect } from 'unistore/preact';
import { actions } from 'actions';
import { QuestionSubType } from 'consts';
import { findInArrayByKeyValue, toObject } from 'utils';
import AutoComplete from 'components/questions/AutoComplete';
import RadioQuestionOption from 'components/questions/RadioQuestionOption';
import RadioQuestionSelectOption from 'components/questions/RadioQuestionSelectOption';
import classNames from 'classnames';

//FIXME: SOF-617 requires the following imports to be moved to components/widget-components.scss
// due to lazy loading of the widget inside an iframe. Keeping this here for potential fix down the road.
// import 'components/questions/RadioQuestion.scss';

const RadioQuestion = ({
  labelId, question, onOptionChanged, isDisabled,
  horizontalOptionsEnabled = false,
  testNoAutoAdvanceEnabled = false,
}) => {
  const { id, options, text, subtype } = question;

  const isImage = subtype === QuestionSubType.Image;
  const isEmoji = subtype === QuestionSubType.Emoji;
  const isSelect = subtype === QuestionSubType.Select;
  const isAutoComplete = subtype === QuestionSubType.AutoComplete;

  const [selectedOptionId, setSelectedOptionId] = useState('');

  const handleRadioSelectChange = (event) => {
    if (isDisabled || !event) {
      return;
    }

    const { value: selectedId } = event.target;

    setSelectedOptionId(selectedId);

    return onOptionChanged({
      option: selectedId && findInArrayByKeyValue(options, 'id', selectedId), autoAdvance: false
    });
  };

  const handleRadioSelectClick = (event) => {
    if (isDisabled || !event) {
      return;
    }
    //NOTE: stopPropagation used to prevent select from closing on first click due to controlled nature
    event.stopPropagation();
  };

  const handleAutoCompleteChoice = (selected) => {
    const { id: selectedId } = toObject(selected);
    const selectedOption = selectedId && findInArrayByKeyValue(options, 'id', selectedId);

    return onOptionChanged({ option: selectedOption, autoAdvance: false });
  };

  const getOptions = () => {
    return options && options.map((optionDetail, index) => {
      const rdoName = `csw-q-rdo-${id}`;
      const rdoKey = `${rdoName}-${optionDetail.id}-${index}`;

      return (isSelect
        ? <RadioQuestionSelectOption key={rdoKey}
                                     i={index}
                                     radioName={rdoName}
                                     option={optionDetail}
                                     isDisabled={isDisabled}/>
        : <RadioQuestionOption key={rdoKey}
                               i={index}
                               radioName={rdoName}
                               option={optionDetail}
                               onRadioSelected={(option, autoAdvance) =>
                                 onOptionChanged({ option, autoAdvance })}
                               isEmoji={isEmoji}
                               isImage={isImage}
                               isDisabled={isDisabled}
                               autoAdvance={!testNoAutoAdvanceEnabled} />);
    }) || [];
  };

  const getAutoCompleteOptionsArray = () => {
    return options && options.map((optionDetail) => {
      return { id: optionDetail.id, text: optionDetail.text };
    }) || [];
  };

  return (
    <fieldset className={classNames('csw-fieldset csw-q-rdo', {
      'csw-q-rdo-img': isImage,
      'csw-q-rdo-emoji': isEmoji,
      'csw-q-rdo-select': isSelect || isAutoComplete,
      'csw-q-rdo-horizontal-options': horizontalOptionsEnabled
    })}>
      <legend className="sr-only" aria-hidden={true}>{text}</legend>
      {isSelect &&
      <select className="csw-select csw-select-90 csw-select-center csw-q-rdo-s outline"
              onClick={handleRadioSelectClick}
              onChange={handleRadioSelectChange}
              disabled={isDisabled}
              aria-labelledby={labelId}
              defaultValue={'placeholder'}
              value={selectedOptionId}>
        <option disabled
                value={'placeholder'}
                className="csw-select-option-placeholder">
          {i18n.t('SELECT_YOUR_ANSWER')}
        </option>
        {getOptions()}
      </select>}

      {isAutoComplete &&
      <AutoComplete options={getAutoCompleteOptionsArray()}
                    onSelectChoice={handleAutoCompleteChoice}/>}

      {!isSelect && !isAutoComplete &&
      <ul className="csw-option-list"
          aria-labelledby={labelId}
          role="radiogroup">
        {getOptions()}
      </ul>}
    </fieldset>
  );
};

RadioQuestion.propTypes /* remove-proptypes */ = {
  labelId: PropTypes.string.isRequired,
  question: PropTypes.object.isRequired,
  onOptionChanged: PropTypes.func,
  isDisabled: PropTypes.bool,
  horizontalOptionsEnabled: PropTypes.bool,
  testNoAutoAdvanceEnabled: PropTypes.bool
};

export default connect('', actions)(RadioQuestion);
